<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :menu-item-component="menuItemComponent"
                           class="menu--section-valentines_day">
    <template v-slot:sectionTitleImage>
      <h2 class="menu--section-title-valentines_day">
        <img src="/library/images/menu/header-valentines-2026.png" alt="Valentine's Day" class="menu--section_logo">
      </h2>
      <div class="menu--section_background"></div>
    </template>
  </default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :menu-item-component="menuItemComponent"
                          class="menu--section-valentines_day">
    <template v-slot:sectionTitleImage>
      <h2 class="menu--section-title-valentines_day">
        <img src="/library/images/menu/header-valentines-2026.png" alt="Valentine's Day" class="menu--section_logo">
      </h2>
      <div class="menu--section_background"></div>
    </template>
  </default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.2.0
 * @copyright  Copyright (C) 2025-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2025-02-10 (1.0.0) (jhoward)
 *    - Copy from Thanksgiving_defaultSection.vue component to ValentinesDay_defaultSection.vue component.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <ValentinesDay_defaultSection>.
 *
 * 2026-02-06 (2.1.0) (jhoward)
 *   - Update header image references to new 2026 asset and adjust alt text.
 *   - Add .menu--section_background div for enhanced section visuals.
 *   - Modify sectionTitle() logic to match new title string and return empty string for 'valentine's day dining'.
 *   - Revise section and background CSS: new padding, background image, sizing, and responsive rules.
 *   - Adjust highlight and description font sizes and colors for improved readability.
 *   - Add mobile-specific styles for section layout and image display.
 *   - Refine visual menu container and section positioning for new assets.
 *
 * 2026-07-20 (2.2.0) (jhoward)
 *   - Update App\Library\Js\Components\Menu\Section\ValentinesDay_defaultSection.vue to use 2026 header image, add background div, and revise layout and responsive styles.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "ValentinesDay_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: 'valentines-day-food-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
  mounted() {
    this.section.ms_title = this.sectionTitle();
    this.section.ms_subtitle = this.sectionSubtitle();
  },
  methods: {
    sectionTitle(): string {
      if (this.section.ms_title.length) {
        if ('valentine\'s day dining' === this.section.ms_title.toLowerCase()) {
          return '';
        }

        return this.section.ms_title;
      }

      return 'DINING';
    },
    sectionSubtitle(): string {
      if (this.section.ms_subtitle.length) {
        return this.section.ms_subtitle;
      }

      return (this.menu?.page?.page_h1 || '').toUpperCase();
    },
  },
})
</script>

<style scoped>
.menu--section-valentines_day {
  font-size: 1.4em;
  color: #231f20;
  padding: 10rem 4.5em 4.5em;
}

.visual_menu--container .menu--section-valentines_day {
  padding-top: 14rem;
}

.visual_menu--container .menu_switcher--container + .visual_menu--page .menu--section-valentines_day:first-child {
  padding-top: 19rem;
}

.menu--section-valentines_day::before {
  content: '';
  position: absolute;
  z-index: -2;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  background-image: url('/library/images/menu/background-valentines-day-2026.jpg');
  background-size: 105% 100%;
  background-position: top center;
  height: auto;
  opacity: 1;
  visibility: visible;
  padding: 0;
}

.visual_menu--container .menu--section-valentines_day::before {
  top: 166px;
}

.menu--section-valentines_day::after {
  content: '';
  position: absolute;
  z-index: -2;
  top: 18.5em;
  left: 4.5em;
  right: 4.5em;
  bottom: 4.5em;
  background-color: #FFF;
}

.visual_menu--container .menu--section-valentines_day::after {
  top: 33.6rem;
}

.menu--section-valentines_day:deep(.menu--highlight) {
  /*color: #ed2030;*/
  font-weight: bold;
}

.menu--section-valentines_day:deep(.menu--item_description h4) {
  font-size: 1.4em;
  color: #000;
}

.menu--section-valentines_day:deep(h2) {
  padding-left: 0;
}

.menu--section-valentines_day:deep(h2 img) {
  width: 80%;
}

.visual_menu--container .menu--section-valentines_day:deep(h2 img) {
  margin-bottom: 1.5em;
}

.menu--section-valentines_day:deep(.menu--section_title) {
  display: none;
  /*font-family: itc-avant-garde-gothic-pro, sans-serif;
  top: -0.65em;
  margin-top: 0;
  letter-spacing: 0.4em;
  font-weight: 300;
  font-size: 2.55em;
  transform: scaleY(0.85);
  padding-left: 0;*/
}

.menu--section-valentines_day:deep(.menu--section_subtitle) {
  text-align: center;
  font-family: trajan-pro-3, serif;
  font-size: 2em;
  margin: 0.3em auto 0;
  line-height: 1em;
  letter-spacing: initial;
}

.menu--section-valentines_day:deep(.visual_menu--page_section.visual_menu--page_section-top),
.menu--section-valentines_day:deep(.menu--page_content-top) {
  padding-top: 1.25em;
  padding-bottom: 1.25em;
  width: 65%;
  margin: 1.25em auto;
  border-top: 0.125em solid #ed2030;
  border-bottom: 0.125em solid #ed2030;
  text-align: center;
}

.menu--section-valentines_day:deep(.visual_menu--page_section.visual_menu--page_section-top p) {
  margin-top: 0;
  margin-bottom: 0;
}

.menu--section-valentines_day:deep(.visual_menu--page_section-bottom .visual_menu--page_section-bottom),
.menu--section-valentines_day:deep(.menu--page_content-bottom) {
  padding-top: 1.25em;
  position: relative;
  text-align: center;
}

.menu--section-valentines_day:deep(.visual_menu--page_section-bottom .visual_menu--page_section-bottom::before),
.menu--section-valentines_day:deep(.menu--page_content-bottom::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 60%;
  height: 0.125em;
  transform: translateX(-50%);
  background-color: #ed2030;
}

.menu--section_background {
  position: absolute;
  top: 17.5em;
  left: 3.7em;
  right: 3.7em;
  bottom: 3.5em;
  z-index: -1;
  border: 0.1em solid #fff;
}

.visual_menu--container .menu--section_background {
  top: 21em;
}

.menu--section-valentines_day:deep(.menu-tag-legend) {
  display: none;
}

.menu--section-title-valentines_day {
  min-height: 250px;
  text-align: center;
}

@media screen and (max-width: 760px) {
  .menu--section-valentines_day {
    padding-top: 0;
    padding-left: 0.5em;
    padding-right: 0.5em;
  }

  .menu--section_logo {
    width: 100%;
  }

  .menu--section-valentines_day::before,
  .menu--section_background {
    display: none;
  }

  .menu--section-title-valentines_day {
    min-height: initial;
  }

  .menu--section-valentines_day:deep(.menu--section_subtitle) {
    word-break: normal;
    padding: 0;
  }
}
</style>
<style>
html body .menu--container-the_steak_house {
  background-image: none;
}
</style>